import { defineStore } from "pinia";
import { ref } from "vue";

import {
  feedbackButtonClicked,
  feedbackDismissed,
  feedbackSent,
} from "@/analytics/events/feedback";
import { capturePosthog, getPosthogSurveys } from "@/analytics/impl/posthog";
import { trackEvent } from "@/analytics/track";
import { captureException } from "@/error/sentry";
import type { FeedbackFeature, FeedbackResponse, FeedbackSurvey } from "@/model/feedback";
import { asReadonly } from "@/utils/vue";

export const useFeedbackStore = defineStore("feedback", () => {
  const survey = ref<FeedbackSurvey>();
  let initialization = 0;

  function load(surveyId: string) {
    reset();
    if (!surveyId) return;
    const currentInitialization = initialization;
    // Fetch the API survey directly so feedback remains available after a response.
    // Force reload to pick up question changes instead of using the cached definition.
    getPosthogSurveys((surveys) => {
      if (currentInitialization !== initialization) return;
      const loadedSurvey = surveys.find(({ id }) => id === surveyId);
      if (!loadedSurvey) return;
      const ratingQuestionId = loadedSurvey.questions.find(({ type }) => type === "rating")?.id;
      const textQuestionId = loadedSurvey.questions.find(({ type }) => type === "open")?.id;
      if (!ratingQuestionId || !textQuestionId) {
        captureException(new Error("Feedback survey requires rating and open questions with IDs"), {
          feedback: { surveyId },
        });
        return;
      }
      survey.value = {
        id: loadedSurvey.id,
        name: loadedSurvey.name,
        ratingQuestionId,
        textQuestionId,
      };
    }, true);
  }

  function show(feature: FeedbackFeature) {
    capturePosthog("survey shown", { $survey_id: survey.value!.id, feature });
    trackEvent(feedbackButtonClicked(feature));
  }

  function dismiss(feature: FeedbackFeature) {
    capturePosthog("survey dismissed", { $survey_id: survey.value!.id, feature });
    trackEvent(feedbackDismissed(feature));
  }

  function send(feature: FeedbackFeature, { rating, text }: FeedbackResponse) {
    const feedbackSurvey = survey.value!;
    const trimmedText = text.trim();
    capturePosthog("survey sent", {
      $survey_id: feedbackSurvey.id,
      $survey_name: feedbackSurvey.name,
      // PostHog bills per unique submission id
      $survey_submission_id: crypto.randomUUID(),
      $survey_completed: true,
      // Both questions are optional, so a skipped one is left out
      ...(rating && { [`$survey_response_${feedbackSurvey.ratingQuestionId}`]: rating }),
      ...(trimmedText && { [`$survey_response_${feedbackSurvey.textQuestionId}`]: trimmedText }),
      feature,
    });
    trackEvent(feedbackSent(feature));
  }

  function reset() {
    initialization++;
    survey.value = undefined;
  }

  return { survey: asReadonly(survey), load, show, dismiss, send, reset };
});
